import React, { useEffect, useContext, useState } from 'react';
import moment from 'moment';
import { SaveButton, useNotify } from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import MuiDialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import InfoIcon from '@material-ui/icons/Info';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import { apiPost } from '../brainApi';
import { QuickAppointmentContext } from '../Context';
import Search from './Customer/Search';
import QuickAppointmentStepper from './QuickAppointmentStepper';
import CostPreview from './CostPreview';
import useStyles from './styles';
import dataProvider from '../dataProvider';
import {
  authorizeGoogle,
  getZ3PConfigValue,
  perPage,
  tokenize,
} from '../helper';
import { sendAnalyticsEvent } from '../analytics';
import DialogTitle from '../components/DialogTitle';
import AdditionalPhoneNumbers from './AdditionalPhoneNumbers';
import SquareCCInput from './SquareCCInput';
import GoogleAuthModal from '../components/GoogleAuthModal';

const QuickAppointmentLayout = (props) => {
  const {
    activeStep,
    addressData,
    contactData,
    vehicleData,
    serviceData,
    couponCode,
    customDiscountReason,
    customDiscountAmount,
    scheduleData,
    toggle,
    handleClose,
    customerType,
    note,
    setNote,
    savedData,
    paymentMethodId,
    setPaymentMethodId,
    paymentMethodList,
    setPaymentMethodList,
    isRecurring,
    rruleAttributes,
    datesToCreate,
    setIsRecurring,
    rruleCombined,
    allSteps,
    quoteId,
  } = useContext(QuickAppointmentContext);
  const classes = useStyles();
  const notify = useNotify();
  const { setNewScheduledWorkID } = props;
  const confirmationEmailTemplateId = getZ3PConfigValue(
    'confirmation_postmark_template_id',
  );
  const confirmationSmsTemplateId = getZ3PConfigValue(
    'appointment_confirmation_sms_template_id',
  );
  const allowAdditionalUserSmsCommunications = getZ3PConfigValue(
    'allow_additional_user_sms_communications',
  );

  const [isCreating, setIsCreating] = useState(false);
  const [sendConfirmationToCustomer, setSendConfirmationToCustomer] = useState(
    true,
  );
  const [sendConfirmationToAdmin, setSendConfirmationToAdmin] = useState(false);
  const [sendAdditionalSms, setSendAdditionalSms] = useState(false);
  const [subtotalDollars, setSubtotalDollars] = useState(null);
  const [couponAmount, setCouponAmount] = useState(null);
  const [shopFee, setShopFee] = useState(null);
  const [salesTaxRate, setSalesTaxRate] = useState(null);
  const [salesTaxAmountDollars, setSalesTaxAmountDollars] = useState(null);
  const [
    minimumServiceChargeAmountDollars,
    setMinimumServiceChargeAmountDollars,
  ] = useState(null);
  const [totalAmountDollars, setTotalAmountDollars] = useState(null);
  const [additionalContactList, setAdditionalContactList] = useState([]);
  // Only used for the additional phone number list
  const [isPhoneNumberListValid, setIsPhoneNumberListValid] = useState(true);
  const [showCcFields, setShowCcFields] = useState(false);
  const [card, setCard] = useState(null);
  const [hasCardError, setHasCardError] = useState(false);
  const [googleAuthModelOpen, setGoogleAuthModelOpen] = useState(false);
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState(null);
  const [googleCalendarIsAuthorized, setGoogleCalendarIsAuthorized] = useState(
    null,
  );

  const selectedSquare =
    paymentMethodId === paymentMethodList.find((pm) => pm.is_square)?.id;

  useEffect(() => {
    const googleCalendarEmailZ3P = getZ3PConfigValue('google_calendar_email');
    const googleCalendarIsAuthorizedZ3P = getZ3PConfigValue(
      'google_calendar_is_authorized',
    );
    setGoogleCalendarEmail(googleCalendarEmailZ3P);
    setGoogleCalendarIsAuthorized(googleCalendarIsAuthorizedZ3P);
  }, []);

  useEffect(() => {
    if (note) {
      setTimeout(() => setNote(null), 3000);
    }
  }, [note, setNote]);

  useEffect(() => {
    if (!addressData || !serviceData) return;
    const addressId =
      addressData?.address_id || addressData?.customer_address_id;
    const params = {
      address_id: addressId,
      services_data: JSON.stringify(serviceData.newServices),
      coupon_code: couponCode || '',
      custom_discount_amount_dollars: customDiscountAmount || 0,
    };
    apiPost('/scheduled-work/cost-preview', params).then(
      ({
        Data: {
          subtotal_dollars,
          coupon_amount_dollars,
          sales_tax_rate_percent,
          sales_tax_dollars,
          shop_fee_dollars,
          minimum_service_charge_amount_dollars,
          total_amount_dollars,
        },
      }) => {
        setSubtotalDollars(subtotal_dollars);
        setCouponAmount(coupon_amount_dollars);
        setShopFee(shop_fee_dollars);
        setSalesTaxRate(sales_tax_rate_percent);
        setSalesTaxAmountDollars(sales_tax_dollars);
        setMinimumServiceChargeAmountDollars(
          minimum_service_charge_amount_dollars,
        );
        setTotalAmountDollars(total_amount_dollars);
      },
    );
  }, [
    addressData,
    serviceData,
    couponCode,
    customDiscountAmount,
    minimumServiceChargeAmountDollars,
  ]);

  useEffect(() => {
    dataProvider
      .getList('payment-method', {
        filter: { is_active: true },
        sort: { field: 'payment_method_id', order: 'ASC' },
        pagination: { page: 1, perPage },
      })
      .then(({ data: paymentMethods }) => {
        setPaymentMethodList(paymentMethods);
        if (totalAmountDollars === 0) {
          setPaymentMethodId(null);
        } else if (paymentMethods && paymentMethods.length > 0) {
          setPaymentMethodId(paymentMethods[0].payment_method_id);
        } else {
          setPaymentMethodId(null);
        }
      });
  }, [totalAmountDollars]);

  const noteColor = () => {
    switch (note?.type) {
      case 'error':
        return '#f44336';
      case 'success':
        return '#00b300';
      default:
        return undefined;
    }
  };

  const createAppointment = ({
    customer_id,
    customer_address_id,
    client_location_id,
    customer_vehicle_id,
    keyosk_id,
    parking_id,
    box,
    additional_phone_numbers,
    square_card_id,
  }) => {
    const { selected_date } = scheduleData || {};
    const appointmentLocation = client_location_id
      ? { client_location_id, keyosk_id, parking_id, box }
      : { customer_address_id };
    const payload = {
      ...scheduleData,
      services: serviceData.newServices,
      status: 'scheduled',
      coupon_code: couponCode,
      custom_discount_reason: customDiscountReason,
      custom_discount_amount_dollars: customDiscountAmount,
      is_created_by_admin: true,
      ...appointmentLocation,
      date: moment(selected_date).format('YYYY-MM-DD'),
      customer_id,
      vehicle_id: customer_vehicle_id,
      send_confirmation_to_customer:
        confirmationEmailTemplateId && sendConfirmationToCustomer,
      send_confirmation_email_to_admin:
        confirmationEmailTemplateId && sendConfirmationToAdmin,
      payment_method_id: paymentMethodId || null,
      is_fleet_service: contactData.is_fleet_user,
      additional_phone_numbers,
      square_card_id,
      quote_id: quoteId,
    };
    // The rruleCombined split will return "every week", "every 2 weeks", or "every month"
    const recurringPatternString = rruleCombined.toText().split(' on ')[0];
    const recurringNote = `Repeat appointments (${recurringPatternString}) end on ${moment(
      rruleAttributes.until,
    ).format('MMMM DD')}.`;
    if (isRecurring) {
      payload.date = moment(rruleAttributes.dtstart).format('YYYY-MM-DD');
      payload.admin_note = `${recurringNote} ${
        scheduleData.admin_note ? scheduleData.admin_note : ''
      }`;
      // Instead of sending confirmation emails, we will send a single recurring confirmation email
      payload.send_confirmation_to_customer = false;
      payload.send_confirmation_email_to_admin = false;
      payload.is_recurring = true;
    }
    apiPost('/scheduled-work', payload)
      .then((json) => {
        setIsCreating(false);
        if (json.Status === 'Failure') {
          setNote({ text: 'Something went wrong', type: 'error' });
          console.log('Error: ', json?.Message);
        } else {
          setNewScheduledWorkID(json.scheduled_work_id);
          handleClose();
          setSendConfirmationToCustomer(true);
          setSendConfirmationToAdmin(false);
          setSubtotalDollars(null);
          setCouponAmount(null);
          setShopFee(null);
          setSalesTaxRate(null);
          setSalesTaxAmountDollars(null);
          setTotalAmountDollars(null);
          setMinimumServiceChargeAmountDollars(null);
          setAdditionalContactList([]);
          if (paymentMethodList && paymentMethodList.length > 0) {
            setPaymentMethodId(paymentMethodList[0].payment_method_id);
          } else {
            setPaymentMethodId(null);
          }
          // For recurring, take the initial appointment (used start date) and use it to copy to additional dates
          if (isRecurring) {
            const dateStrings = datesToCreate.map((date) =>
              moment(date).format('YYYY-MM-DD'),
            );
            const rruleFirstDate = moment(rruleAttributes.dtstart).format(
              'YYYY-MM-DD',
            );
            const index = dateStrings.indexOf(rruleFirstDate);
            // Since we have already created the appointment for the start date, remove it from the date list
            if (index >= 0) {
              dateStrings.splice(index, 1);
            }
            if (dateStrings.length === 0) {
              setIsRecurring(false);
              return;
            }
            const payload = {
              existing_scheduled_work_id: json.scheduled_work_id,
              dates: dateStrings,
              service_vehicle_id: json.service_vehicle_id,
              start_date: rruleFirstDate,
              recurring_pattern_string: recurringPatternString,
            };
            apiPost('/bulk-scheduled-work', payload)
              .then((response) => {
                if (response.Status === 'Success') {
                  // Add one to date length to include the initial appointment not created with the bulk request
                  notify(
                    `${dateStrings.length +
                      1} Repeat Appointments have been created`,
                  );
                } else {
                  notify(
                    `Error creating repeat appointments: ${response.Message}`,
                  );
                }
              })
              .catch(() => {
                notify(
                  'There was an error creating repeat appointments. Please reach out to your Zippity representative.',
                );
                sendAnalyticsEvent('createAppointmentFailure', {
                  isRecurring,
                });
              })
              .finally(() => {
                setNewScheduledWorkID(null);
              });
          }
        }
      })
      .catch((error) => {
        setIsCreating(false);
        console.log(error);
        sendAnalyticsEvent('createAppointmentFailure', {
          isRecurring,
        });
      });
    sendAnalyticsEvent('createAppointmentSuccess', {
      isRecurring,
    });
  };

  const addCreditCardToCustomer = (token) => {
    const payload = {
      square_token: token,
    };
    return apiPost(`/add-square-card/${contactData.id}`, payload).then(
      (json) => {
        if (json.success) {
          setNote({ text: 'Card added', type: 'success' });
          return json.square_card_id;
        }
        throw new Error(json.message);
      },
    );
  };

  const submitCard = async (card_) => {
    const token = await tokenize(card_);
    const squareCardId = await addCreditCardToCustomer(token);
    return squareCardId;
  };

  const formatAppointmentData = async () => {
    setIsCreating(true);
    const additionalPhoneNumbers = [];
    additionalContactList.map((item) => {
      if (item.phoneNumber !== '' && item.phoneNumber.length === 10) {
        additionalPhoneNumbers.push(item?.phoneNumber);
      }
    });
    const {
      customer_address_id: addressDataCustomerAddressId,
      client_location_id: addressDataClientLocationId,
      keyosk_id: addressDataKeyoskId,
      parking_id: addressDataParkingId,
      box: addressDataBox,
    } = addressData;
    let squareCardId = null;
    if (selectedSquare && card) {
      try {
        squareCardId = await submitCard(card);
      } catch {
        setNote({ text: 'Failed to add card', type: 'error' });
        setIsCreating(false);
        return;
      }
    }
    if (customerType === 'existing') {
      createAppointment({
        customer_id: contactData.id,
        customer_address_id: addressDataCustomerAddressId,
        client_location_id: addressDataClientLocationId,
        keyosk_id: addressDataKeyoskId,
        parking_id: addressDataParkingId,
        box: addressDataBox,
        customer_vehicle_id: vehicleData?.vehicle_id,
        additional_phone_numbers: additionalPhoneNumbers,
        square_card_id: squareCardId,
      });
    } else {
      const {
        customer_id,
        customer_address_id: savedCustomerAddressId,
        client_location_id: savedClientLocationId,
        parking_id: savedParkingId,
        keyosk_id: savedKeyoskId,
        box: savedBox,
        customer_vehicle_id,
      } = savedData;
      createAppointment({
        customer_id,
        customer_address_id:
          savedCustomerAddressId || addressDataCustomerAddressId,
        client_location_id:
          savedClientLocationId || addressDataClientLocationId,
        parking_id: savedParkingId || addressDataParkingId,
        keyosk_id: savedKeyoskId || addressDataKeyoskId,
        box: savedBox || addressDataBox,
        customer_vehicle_id,
        additional_phone_numbers: additionalPhoneNumbers,
        square_card_id: squareCardId,
      });
    }
  };

  const handleCloseDialog = () => {
    // isRecurring should not be within handleClose because we want to wait to reset it until all recurring appointments have been created
    setIsRecurring(false);
    handleClose();
  };

  const handleAddAdditionalContact = () => {
    const temp = [...additionalContactList];
    temp.push({ phoneNumber: '' });
    setAdditionalContactList(temp);
  };

  const onClickChangeAccount = () => {
    authorizeGoogle(() => setGoogleAuthModelOpen(true));
  };

  return (
    <>
      <Dialog
        onClose={handleCloseDialog}
        disableBackdropClick
        disableEscapeKeyDown
        aria-labelledby="simple-dialog-title"
        open={toggle}
        className={classes.dialogRoot}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle onClose={handleCloseDialog}>
          Create Appointment
        </DialogTitle>
        <Divider />
        <MuiDialogContent>
          <Box display="flex" alignItems="center" justifyContent="center">
            {!contactData && <Search />}
          </Box>
          <QuickAppointmentStepper />
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            {activeStep >= allSteps.indexOf('Cost Preview') && scheduleData && (
              <>
                <CostPreview
                  subtotalDollars={subtotalDollars}
                  shopFee={shopFee}
                  salesTaxRate={salesTaxRate}
                  salesTaxAmountDollars={salesTaxAmountDollars}
                  minimumServiceChargeAmountDollars={
                    minimumServiceChargeAmountDollars
                  }
                  couponAmount={couponAmount}
                  totalAmountDollars={totalAmountDollars}
                />
                <br />
                {totalAmountDollars > 0 && (
                  <>
                    {paymentMethodList && paymentMethodList.length === 1 && (
                      <Typography variant="h6" gutterBottom>
                        Payment Method: {paymentMethodList[0].short_description}
                      </Typography>
                    )}
                    {paymentMethodList && paymentMethodList.length > 1 && (
                      <>
                        <span>
                          <Typography variant="h6" gutterBottom>
                            Payment Method
                          </Typography>
                        </span>
                        <Select
                          className="payment-method-select"
                          size="large"
                          value={paymentMethodId || null}
                          onChange={(event) =>
                            setPaymentMethodId(event.target.value)
                          }
                        >
                          {paymentMethodList.map(
                            ({ payment_method_id, short_description }) => (
                              <MenuItem
                                key={payment_method_id}
                                value={payment_method_id}
                              >
                                {short_description}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </>
                    )}
                    {selectedSquare && showCcFields && (
                      <SquareCCInput
                        card={card}
                        setCard={setCard}
                        setShow={setShowCcFields}
                        setHasError={setHasCardError}
                        responsive
                        cancellable
                      />
                    )}
                    {selectedSquare && !showCcFields && (
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setShowCcFields(true)}
                        style={{ marginTop: '5px' }}
                      >
                        Add credit card
                      </Button>
                    )}
                  </>
                )}
                <Box mt={2} ml={1} mb={0.8}>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    width="100%"
                  >
                    <Box ml={1} mb={0.8} width="100%">
                      <Box display="flex" flexDirection="column">
                        {!isRecurring && confirmationEmailTemplateId && (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={sendConfirmationToCustomer}
                                  name="sendConfirmationToCustomer"
                                  color="primary"
                                />
                              }
                              label={`Send confirmation ${[
                                confirmationEmailTemplateId &&
                                  contactData?.email &&
                                  'email',
                                confirmationSmsTemplateId && 'SMS',
                              ]
                                .filter(Boolean)
                                .join(' and ')} to customer`}
                              onChange={(e) =>
                                setSendConfirmationToCustomer(e.target.checked)
                              }
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={sendConfirmationToAdmin}
                                  name="sendConfirmationToAdmin"
                                  color="primary"
                                />
                              }
                              label="Send me a copy of the confirmation email"
                              onChange={(e) =>
                                setSendConfirmationToAdmin(e.target.checked)
                              }
                            />
                            {allowAdditionalUserSmsCommunications && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={sendAdditionalSms}
                                    name="allowAdditionalUserSmsCommunications"
                                    color="primary"
                                  />
                                }
                                label="Send SMS service notifications to additional contacts"
                                onChange={(e) => {
                                  setSendAdditionalSms(e.target.checked);
                                  if (e.target.checked) {
                                    handleAddAdditionalContact();
                                  } else {
                                    setAdditionalContactList([]);
                                  }
                                }}
                              />
                            )}
                            {googleCalendarIsAuthorized && (
                              <Typography
                                style={{ paddingTop: 9, paddingBottom: 9 }}
                              >
                                Appointment will be added to Google Calendar (
                                {googleCalendarEmail})
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {sendAdditionalSms && (
                  <AdditionalPhoneNumbers
                    additionalContactList={additionalContactList}
                    setAdditionalContactList={setAdditionalContactList}
                    handleAddAdditionalContact={handleAddAdditionalContact}
                    setIsPhoneNumberListValid={setIsPhoneNumberListValid}
                  />
                )}

                {!isPhoneNumberListValid && (
                  <Typography variant="caption" style={{ color: 'red' }}>
                    Phone numbers in list must be 10 digits
                  </Typography>
                )}
                <SaveButton
                  label={
                    isRecurring
                      ? 'Create Repeat Appointments'
                      : 'Create Appointment'
                  }
                  disabled={
                    isCreating || !isPhoneNumberListValid || hasCardError
                  }
                  className={classes.buttonWithoutIcon}
                  handleSubmitWithRedirect={formatAppointmentData}
                  saving={isCreating}
                />
              </>
            )}
          </Box>
          {note && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              className={classes.infoMessage}
            >
              <InfoIcon
                fontSize="small"
                className={classes.infoIcon}
                htmlColor={noteColor()}
              />
              {note.text}
            </Box>
          )}
        </MuiDialogContent>
      </Dialog>
      <GoogleAuthModal
        onClose={() => setGoogleAuthModelOpen(false)}
        onSuccess={setGoogleCalendarEmail}
        open={googleAuthModelOpen}
      />
    </>
  );
};

export default QuickAppointmentLayout;
